import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, ReplaySubject, Subscription, switchMap, timeout} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {catchError, debounceTime, map} from 'rxjs/operators';
import {TokenService} from './token.service';
import {User} from "../model/User";
import {Company} from "../model/Company";
import {LoginResult} from "../model/LoginResult";
import {environment} from "../../environments/environment";
import {LoginResponse} from "../model/dto/login-response";
import {Role} from "../model/Role";
import {Router} from "@angular/router";
import {RoleEnum} from "../model/enum/role-enum";


@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  public readonly currentUserSubject$ = new ReplaySubject<User>(1);
  public currentUser$ = this.currentUserSubject$.asObservable();
  public isAuthenticated = new BehaviorSubject<boolean>(false);

  private storageEventSubscription: Subscription;
  public company: Company = undefined;

  private account$ = this.http.get<User>(environment.apiUrl + '/account');

  private roles: Role[];

  public loading = new BehaviorSubject<boolean>(true);

  constructor(private http: HttpClient,
              private router: Router,
              private tokenService: TokenService) {
    if (this.tokenService.getToken() !== null) {
      this.account$
        .pipe(
          timeout(5000) //5 seconds
        )
        .subscribe({
          next: account => {
            if (account) {
              this.currentUserSubject$.next(account);
              this.company = account.company;
              this.roles = account.authorities;
              this.isAuthenticated.next(true);
              this.loading.next(false)
            }
          }, error: err => {
            this.isAuthenticated.next(false);
            this.loading.next(false);
            router.navigate(['/login']);
          }
        });
    } else {
      this.isAuthenticated.next(false);
      this.loading.next(false)
    }
  }

  login(username: string, password: string, rememberMe: boolean = false): Observable<LoginResult> {
    this._logoutInternal();
    return this.http.post<LoginResponse>(environment.apiUrl + '/login', {username, password, rememberMe})
      .pipe(
        debounceTime(5000),
        map(auth => {
          if (!auth || !auth.id_token) throw new Error('Can\'t get token!');
          this.tokenService.setToken(auth.id_token);
          this.roles = auth.authorities;
        }),
        switchMap(next => {
          return this.account$;
        }),
        map(account => {
          if (account) {
            this.currentUserSubject$.next(account);
            this.company = account.company;
            this.isAuthenticated.next(true);
          }
          return new LoginResult(true);
        }),
        catchError(err => {
          return of(new LoginResult(false, 'Cant login'));
        })
      );
  }

  logout(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.http.post<any>(environment.apiUrl + '/logout', {}).subscribe(val => {
        this._logoutInternal();
        observer.next(true);
        observer.complete();
      }, error => {
        this._logoutInternal();
        observer.next(false);
        observer.complete();
      });
    });
  }

  ngOnDestroy(): void {
    this.storageEventSubscription.unsubscribe();
  }

  public getRoles(): Role[] {
    return this.roles;
  }

  public isPowerUser(): boolean {
    return this.getRoles().findIndex(m => m.authority === RoleEnum.ROLE_POWER_USER) !== -1;
  }

  private _logoutInternal(): void {
    this.tokenService.flushSession();
    this._clearState();
  }

  private _clearState(): void {
    this.isAuthenticated.next(false);
    this.currentUserSubject$.next(undefined);
    this.company = undefined;
  }
}
